/**
 * PreviouslyConnectedSection: history of past accountants shown on the
 * Empty state. Each row carries the person's identity, the reason + date
 * they were archived, an "Invite again" text link (opens Invite drawer
 * pre-filled), and an X icon ("Remove from history").
 *
 * No business logic. Takes handlers from the parent composer:
 *   onReinvite(prev) - opens the invite drawer with name/email/firm pre-filled
 *   onForget(id)     - confirms, then removes from history
 *
 * Section is hidden entirely when the list is empty, so brand-new users
 * see the Empty state exactly as before.
 */

import { useId } from "react";
import { X } from "lucide-react";
import type { PreviousAccountant } from "../myAccountantTypes";
import { formatDateShort, getInitials } from "../myAccountantUtils";
import { PALETTE } from "../constants";

interface PreviouslyConnectedSectionProps {
  previousAccountants: PreviousAccountant[];
  onReinvite: (prev: PreviousAccountant) => void;
  onForget: (previousId: string) => void;
}

export function PreviouslyConnectedSection({
  previousAccountants,
  onReinvite,
  onForget,
}: PreviouslyConnectedSectionProps) {
  const uid = useId().replace(/:/g, "");
  const styleTag = usePreviouslyConnectedStyles(uid);

  if (previousAccountants.length === 0) return null;

  return (
    <>
      {styleTag}
      <section
        className={`pcs-${uid}-sec`}
        aria-label="Previously connected accountants"
      >
        <p className={`pcs-${uid}-eye`}>Previously connected</p>
        <div className={`pcs-${uid}-list`}>
          {previousAccountants.map((prev) => {
            const displayName = prev.name?.trim() || prev.email;
            const initials = getInitials(
              prev.name || prev.email,
              prev.email,
            );
            const archivedShort = formatDateShort(prev.archivedAt);
            const reasonLabel =
              prev.reason === "expired"
                ? `Invite expired ${archivedShort}`
                : prev.reason === "declined"
                  ? `Invite declined ${archivedShort}`
                  : `Removed ${archivedShort}`;
            const reasonClass =
              prev.reason === "expired" || prev.reason === "declined"
                ? `pcs-${uid}-reason-r`
                : `pcs-${uid}-reason`;

            return (
              <div key={prev.id} className={`pcs-${uid}-row`}>
                <div className={`pcs-${uid}-ava`} aria-hidden="true">
                  {initials}
                </div>
                <div className={`pcs-${uid}-id`}>
                  <p className={`pcs-${uid}-name`}>{displayName}</p>
                  <p className={`pcs-${uid}-meta`}>
                    {prev.email} &middot;{" "}
                    <span className={reasonClass}>{reasonLabel}</span>
                  </p>
                </div>
                <button
                  type="button"
                  className={`pcs-${uid}-invite`}
                  onClick={() => onReinvite(prev)}
                >
                  Invite again
                </button>
                <button
                  type="button"
                  className={`pcs-${uid}-close`}
                  onClick={() => onForget(prev.id)}
                  aria-label={`Remove ${displayName} from history`}
                  title="Remove from history"
                >
                  <X size={13} strokeWidth={2} />
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

function usePreviouslyConnectedStyles(uid: string) {
  const css = `
    .pcs-${uid}-sec {
      padding-top: 28px;
      border-top: 1px solid ${PALETTE.line};
      margin-top: 8px;
    }
    .pcs-${uid}-eye {
      font-size: 10.5px;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      color: ${PALETTE.ink3};
      font-weight: 700;
      margin: 0 0 10px;
      text-align: center;
    }
    .pcs-${uid}-list {
      max-width: 540px;
      margin: 0 auto;
      background: #ffffff;
      border: 1px solid ${PALETTE.line};
      border-radius: 10px;
      overflow: hidden;
    }
    .pcs-${uid}-row {
      padding: 10px 14px;
      display: grid;
      grid-template-columns: 28px 1fr auto auto;
      gap: 12px;
      align-items: center;
      border-top: 1px solid ${PALETTE.lineSoft};
      transition: background 140ms;
    }
    .pcs-${uid}-row:first-child { border-top: 0; }
    .pcs-${uid}-row:hover { background: ${PALETTE.page}; }
    .pcs-${uid}-ava {
      width: 28px; height: 28px;
      border-radius: 50%;
      background: ${PALETTE.ink};
      color: #fff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 10.5px;
      font-weight: 600;
    }
    .pcs-${uid}-id { min-width: 0; overflow: hidden; }
    .pcs-${uid}-name {
      font-size: 12.5px;
      font-weight: 600;
      color: ${PALETTE.ink};
      margin: 0 0 1px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .pcs-${uid}-meta {
      font-size: 11px;
      color: ${PALETTE.ink3};
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .pcs-${uid}-reason { color: ${PALETTE.ink3}; font-weight: 500; }
    .pcs-${uid}-reason-r { color: ${PALETTE.red}; font-weight: 500; }
    .pcs-${uid}-invite {
      background: transparent;
      border: 0;
      color: ${PALETTE.teal};
      font-size: 11.5px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      padding: 5px 9px;
      border-radius: 6px;
      transition: background 140ms, color 140ms;
    }
    .pcs-${uid}-invite:hover {
      background: ${PALETTE.tealBg};
      color: ${PALETTE.tealDark};
    }
    .pcs-${uid}-close {
      background: transparent;
      border: 0;
      color: ${PALETTE.ink4};
      padding: 5px;
      border-radius: 6px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: background 140ms, color 140ms;
      font-family: inherit;
    }
    .pcs-${uid}-close:hover {
      background: ${PALETTE.lineSoft};
      color: ${PALETTE.ink};
    }

    @media (max-width: 540px) {
      .pcs-${uid}-row {
        grid-template-columns: 24px 1fr auto;
        gap: 10px;
      }
      .pcs-${uid}-close { grid-column: 3; }
      .pcs-${uid}-invite { grid-column: 1 / -1; justify-self: flex-end; }
    }
  `;
  return <style>{css}</style>;
}